// ─────────────────────────────────────────────────────────────────────────────
// UNIFIED RENDER PIPELINE
// 3D is the PRIMARY path on all devices with WebGL2 support.
//
// Routing logic:
//   WebGL2 available + GLB loads  →  3D path (Three.js + Ammo.js / PBD cloth)
//   GLB fails to load             →  2D PNG fallback (transparent image cloth sim)
//   No WebGL2 at all              →  2D canvas cloth grid physics
//
// 3D path blend: 70% skeletal (RetargetEngine) + 30% cloth physics deformation
// ─────────────────────────────────────────────────────────────────────────────

import * as THREE from "three";
import type { NormalizedLandmark } from "./pose";
import type { GarmentSchema, LoadedGarment } from "./garment-schema";
import { detectDeviceTier, type DeviceTier } from "./device-tier";
import { RetargetEngine, type RetargetPose } from "./retarget";
import { AnchorSystem } from "./anchor-system";
import { loadAmmo, AmmoClothSim } from "./ammo-cloth";
import { PBDClothSim, FABRIC_PRESETS } from "./cloth-physics";
import { buildBodyProxy, BodyCollider, BodyProxyDebug } from "./body-proxy";

// 2D canvas cloth system (from try-on.js cloth engine, ported)
import { Cloth2D } from "./cloth-2d";

export type RenderMode = "2d" | "3d";

export interface UnifiedPipelineOptions {
  canvas:          HTMLCanvasElement;  // overlay canvas for 2D
  threeRenderer:   THREE.WebGLRenderer;
  threeScene:      THREE.Scene;
  threeCamera:     THREE.Camera;
  isMirrored:      boolean;
  tierOverride?:   DeviceTier;
  skeletalWeight?: number;   // default 0.7
  debugProxy?:     boolean;  // show collision proxy wireframe (dev only)
}

export class UnifiedPipeline {
  readonly mode: RenderMode;

  private opts: UnifiedPipelineOptions;
  private retarget: RetargetEngine;

  // ── 3D state ──────────────────────────────────────────────────────────────
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private ammo: any = null;
  private ammoCloth: AmmoClothSim | null = null;
  private anchorSystem: AnchorSystem | null = null;
  private bodyCollider: BodyCollider | null = null;
  private bodyProxyDebug: BodyProxyDebug | null = null;
  private glbRoot: THREE.Group | null = null;

  // ── 2D state ──────────────────────────────────────────────────────────────
  private cloth2D: Cloth2D | null = null;
  private pbdCloth: PBDClothSim | null = null;
  private garmentImage: HTMLImageElement | null = null;

  // ── Common ────────────────────────────────────────────────────────────────
  private activeSchema: GarmentSchema | null = null;
  private lastPose: RetargetPose | null = null;
  private lastDt = 1 / 60;
  private lastTime = 0;

  constructor(opts: UnifiedPipelineOptions) {
    this.opts = opts;

    // 3D is primary. Only fall back to 2D when device tier explicitly returns "low"
    // (which only happens when WebGL2 is completely absent — see device-tier.ts).
    const tier = opts.tierOverride ?? detectDeviceTier().tier;
    this.mode  = tier === "low" ? "2d" : "3d";

    this.retarget = new RetargetEngine(opts.isMirrored);

    console.log(`[UnifiedPipeline] mode=${this.mode} (tier=${tier})`);
  }

  // ── Load a garment into the active pipeline ───────────────────────────────
  async setGarment(loaded: LoadedGarment) {
    this._teardownGarment();
    this.activeSchema = loaded.schema;

    if (this.mode === "3d") {
      if (loaded.glbScene) {
        // Full 3D path — rigged GLB + cloth physics
        await this._setup3D(loaded);
      } else {
        // GLB not available (file missing or 404) — degrade to 2D PNG cloth
        console.warn(
          `[UnifiedPipeline] GLB not loaded for "${loaded.schema.id}" — using 2D PNG fallback`
        );
        this._setup2D(loaded);
      }
    } else {
      // Device has no WebGL2 — 2D only
      this._setup2D(loaded);
    }
  }

  // ── Per-frame update — call from RAF loop ─────────────────────────────────
  update(
    landmarks: NormalizedLandmark[],
    timestamp: number
  ) {
    const dt = this.lastTime > 0
      ? Math.min((timestamp - this.lastTime) / 1000, 0.05)
      : 1 / 60;
    this.lastTime = timestamp;
    this.lastDt   = dt;

    const pose = this.retarget.extractPose(landmarks, timestamp);
    if (!pose) return;
    this.lastPose = pose;

    if (this.mode === "3d") {
      this._update3D(pose, dt, landmarks);
    } else {
      this._update2D(pose, landmarks);
    }
  }

  // ── Switch mirroring (front/rear camera flip) ─────────────────────────────
  setMirrored(m: boolean) {
    this.opts.isMirrored = m;
    this.retarget.setMirrored(m);
  }

  dispose() {
    this._teardownGarment();
    this.retarget.reset();
  }

  // ── 3D SETUP ─────────────────────────────────────────────────────────────
  private async _setup3D(loaded: LoadedGarment) {
    const schema = loaded.schema;
    const scene  = loaded.glbScene!;

    // Clone so we can reuse the cached original
    this.glbRoot = scene.clone(true);
    this.opts.threeScene.add(this.glbRoot);

    // Bind bones to retargeting engine
    this.retarget.bindGLB(this.glbRoot);

    // Apply texture if available
    if (loaded.texture) {
      this.glbRoot.traverse(obj => {
        if (obj instanceof THREE.SkinnedMesh) {
          const mat = new THREE.MeshStandardMaterial({
            map: loaded.texture,
            transparent: true,
            alphaTest: 0.04,
            depthWrite: true,
            side: THREE.DoubleSide,
          });
          obj.material = mat;
        }
      });
    }

    // ── Load Ammo.js and build soft body ──────────────────────────────────
    this.ammo = await loadAmmo();
    if (this.ammo) {
      // 16×24 cloth grid sized to garment
      this.ammoCloth = new AmmoClothSim(
        this.ammo,
        16,
        24,
        schema.physics.stiffness * 0.8 + 0.1,  // width ≈ torso width
        schema.physics.drape * 1.0 + 0.5        // height ≈ torso height
      );

      // Apply fabric physics from schema
      this.ammoCloth.setFabric({
        stiffness: schema.physics.stiffness,
        damping:   schema.physics.damping,
        mass:      schema.physics.mass,
      });

      // Add cloth mesh to Three.js scene
      const clothMat = new THREE.MeshStandardMaterial({
        map: loaded.texture ?? undefined,
        transparent: true,
        alphaTest: 0.04,
        side: THREE.DoubleSide,
        depthWrite: true,
      });
      const clothMesh = new THREE.Mesh(this.ammoCloth.geometry, clothMat);
      clothMesh.frustumCulled = false;
      this.opts.threeScene.add(clothMesh);

      // Build anchor system — one rigid body per unique bone in anchors
      const uniqueBones = [...new Set(schema.anchors.map(a => a.bone))];
      if (this.lastPose) {
        this.anchorSystem = new AnchorSystem(this.ammo, this._getAmmoWorld());
        this.anchorSystem.buildRigidBodies(this.lastPose, uniqueBones);
        this.anchorSystem.bindAnchors(this.ammoCloth["softBody"], schema.anchors);
      }

      // Build body collider — 14 kinematic capsules the cloth drapes over
      this.bodyCollider = new BodyCollider(this.ammo, this._getAmmoWorld());

      // Debug proxy wireframe (dev mode)
      if (this.opts.debugProxy) {
        this.bodyProxyDebug = new BodyProxyDebug(this.opts.threeScene);
      }

    } else {
      // Ammo failed — fall back to PBD soft cloth (no anchor binding)
      console.warn("[UnifiedPipeline] Ammo unavailable, using PBD for 3D cloth");
      this.pbdCloth = new PBDClothSim(
        16, 24, 0.6, 0.8,
        FABRIC_PRESETS[schema.physics.stiffness > 0.7 ? "denim" : "cotton"]
      );
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private _getAmmoWorld(): any {
    // AmmoClothSim exposes its world via a private field — access via bracket notation
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.ammoCloth as any)["world"];
  }

  // ── 3D PER-FRAME ─────────────────────────────────────────────────────────
  private _update3D(pose: RetargetPose, dt: number, landmarks: NormalizedLandmark[]) {
    const skW = this.opts.skeletalWeight ?? 0.7;

    // ── Body proxy — sync collision capsules to landmark positions ───────────
    // Must happen BEFORE cloth step so Ammo sees updated body positions
    const proxy = buildBodyProxy(landmarks, this.opts.isMirrored);
    if (proxy) {
      this.bodyCollider?.sync(proxy);
      this.bodyProxyDebug?.update(proxy);
    }

    // ── Drive skeleton (70% skeletal control) ─────────────────────────────
    if (this.glbRoot) {
      this.retarget.applyPose(pose, this.glbRoot, skW);
    }

    // ── Cloth physics step (30% deformation) ─────────────────────────────
    if (this.ammoCloth && this.activeSchema) {
      this.ammoCloth.pinToSkeleton(
        this._poseToLandmarks(pose),
        this.opts.isMirrored
      );

      // Move anchor rigid bodies to follow skeleton
      this.anchorSystem?.updateRigidBodies(pose);

      // Step — body collider capsules are already in the Ammo world,
      // the soft body automatically collides against them
      this.ammoCloth.step(dt);
    } else if (this.pbdCloth && this.activeSchema) {
      this.pbdCloth.pinToSkeleton(
        this._poseToLandmarks(pose),
        this.opts.isMirrored
      );
      // PBD: apply body proxy as collision constraint
      if (proxy) {
        this._applyProxyToPBD(proxy);
      }
      this.pbdCloth.step(dt);
    }

    // Render
    this.opts.threeRenderer.render(
      this.opts.threeScene,
      this.opts.threeCamera
    );
  }

  // ── Apply body proxy collision to PBD cloth (when Ammo not available) ──────
  // Pushes cloth particles outside proxy sphere volumes each step.
  private _applyProxyToPBD(proxy: import("./body-proxy").BodyProxy) {
    if (!this.pbdCloth) return;
    // Access PBD particles via internal field
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const particles: Array<{ pos: THREE.Vector3 }> = (this.pbdCloth as any).particles ?? [];
    for (const p of particles) {
      for (const shape of proxy.shapes) {
        const diff = p.pos.clone().sub(shape.center);
        const dist = diff.length();
        const minDist = shape.radius + 0.01; // 1cm gap
        if (dist < minDist && dist > 0.0001) {
          // Push particle out of sphere/capsule volume
          p.pos.copy(shape.center).addScaledVector(diff.normalize(), minDist);
        }
      }
    }
  }

  // ── 2D SETUP ─────────────────────────────────────────────────────────────
  private _setup2D(loaded: LoadedGarment) {
    this.garmentImage = loaded.image;
    const schema = loaded.schema;

    const { canvas } = this.opts;
    this.cloth2D = new Cloth2D(canvas, {
      cols: 12,
      rows: 20,
      stiffness: schema.physics.stiffness,
      drape:     schema.physics.drape,
      wind:      schema.physics.wind,
    });

    // PBD as physics backend for 2D
    const preset = schema.physics.stiffness > 0.7 ? "denim"
                 : schema.physics.drape      > 0.5 ? "silk"
                 : "cotton";
    this.pbdCloth = new PBDClothSim(
      12, 20, 0.6, 0.8,
      FABRIC_PRESETS[preset as keyof typeof FABRIC_PRESETS] ?? FABRIC_PRESETS.cotton
    );
  }

  // ── 2D PER-FRAME ─────────────────────────────────────────────────────────
  private _update2D(pose: RetargetPose, landmarks: NormalizedLandmark[]) {
    if (!this.cloth2D || !this.garmentImage || !this.activeSchema) return;

    this.pbdCloth?.pinToSkeleton(landmarks, this.opts.isMirrored);
    this.pbdCloth?.step(this.lastDt);

    this.cloth2D.draw(
      this.garmentImage,
      pose,
      this.activeSchema,
      this.opts.isMirrored
    );
  }

  // ── TEARDOWN ─────────────────────────────────────────────────────────────
  private _teardownGarment() {
    // 3D cleanup
    if (this.glbRoot) {
      this.opts.threeScene.remove(this.glbRoot);
      this.glbRoot.traverse(obj => {
        if (obj instanceof THREE.Mesh) {
          obj.geometry?.dispose();
          if (Array.isArray(obj.material)) obj.material.forEach(m => m.dispose());
          else obj.material?.dispose();
        }
      });
      this.glbRoot = null;
    }

    this.anchorSystem?.dispose();
    this.anchorSystem = null;

    this.bodyCollider?.dispose();
    this.bodyCollider = null;

    this.bodyProxyDebug?.dispose();
    this.bodyProxyDebug = null;

    this.ammoCloth?.dispose();
    this.ammoCloth = null;

    // 2D cleanup
    this.cloth2D?.reset();
    this.cloth2D = null;

    this.pbdCloth?.dispose?.();
    this.pbdCloth = null;

    this.garmentImage = null;
    this.activeSchema = null;
    this.lastPose      = null;
  }

  // ── Utility: reconstruct minimal NormalizedLandmark array from RetargetPose
  // Used to pass to PBD/Ammo systems that still expect landmark arrays
  private _poseToLandmarks(pose: RetargetPose): NormalizedLandmark[] {
    // Fill with zeros then overwrite known positions
    const lms: NormalizedLandmark[] = Array.from({ length: 33 }, () => ({
      x: 0, y: 0, z: 0, visibility: 0
    }));

    const toNorm = (v: THREE.Vector3) => ({
      x: v.x / 4 + 0.5,
      y: -v.y / 4 + 0.5,
      z: v.z / -2,
      visibility: 1,
    });

    lms[11] = toNorm(pose.leftShoulder);
    lms[12] = toNorm(pose.rightShoulder);
    // Approximate hip positions from hipCenter
    lms[23] = toNorm(pose.hipCenter.clone().add(new THREE.Vector3(-0.1, 0, 0)));
    lms[24] = toNorm(pose.hipCenter.clone().add(new THREE.Vector3( 0.1, 0, 0)));

    return lms;
  }
}
